import { merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import WaveformData from 'waveform-data';

import { AudioEditor, WaveformOptions } from './AudioEditor';

export default abstract class Waveform {
  protected ctx: CanvasRenderingContext2D;
  protected data: WaveformData;

  protected constructor(
    protected audioEditor: AudioEditor,
    protected waveformOptions: WaveformOptions,
  ) {
    this.data = null as any;
    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = `${waveformOptions.height}px`;
    // canvas.style.borderRadius = '5px';
    waveformOptions.mountPoint.appendChild(canvas);
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;

    this.ctx = canvas.getContext('2d');

    merge(
      this.audioEditor.mixPointChanged$,
      this.audioEditor.outroPointChanged$,
      this.audioEditor.introPointChanged$,
      this.audioEditor.onStartChanged$,
      this.audioEditor.onEndChanged$,
      this.audioEditor.onPlayerChanged$,
    )
      .pipe(takeUntil(this.audioEditor.unsubscribeAll))
      .subscribe(() => {
        this.refresh();
      });

    audioEditor.onWaveformReadyToDraw$.subscribe((waveform: WaveformData) => {
      try {
        this.data = waveform.resample({ width: this.ctx.canvas.clientWidth });
      } catch (e) {
        this.data = waveform.resample({ scale: 256 });
      }
      this.refresh();
    });
  }

  protected refresh() {
    if (!this.data) {
      return;
    }

    this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
    this.drawBackground();
    this.drawWaveform(
      this.ctx,
      this.data,
      this.waveformOptions.height,
      this.waveformOptions.padding,
    );
    this.drawSelectionBox(this.ctx);
    this.drawCursor(this.ctx, this.audioEditor.player.time);
    if (this.audioEditor.segmentBar.isMixActive) {
      this.drawMixCursor(this.ctx, this.audioEditor.segmentBar.mixPoint);
    }
    if (this.audioEditor.segmentBar.isIntroActive) {
      this.drawIntroCursor(this.ctx, this.audioEditor.segmentBar.introPoint);
    }
    if (this.audioEditor.segmentBar.isOutroActive) {
      this.drawOutroCursor(this.ctx, this.audioEditor.segmentBar.outroPoint);
    }
  }

  abstract drawBackground(): void;
  abstract drawWaveform(
    ctx: CanvasRenderingContext2D,
    waveform: WaveformData,
    height: number,
    padding: number,
  ): void;

  abstract drawSelectionBox(ctx: CanvasRenderingContext2D): void;
  abstract drawCursor(ctx: CanvasRenderingContext2D, instant: number): void;

  abstract drawMixCursor(ctx: CanvasRenderingContext2D, instant: number): void;
  abstract drawIntroCursor(ctx: CanvasRenderingContext2D, instant: number): void;
  abstract drawOutroCursor(ctx: CanvasRenderingContext2D, instant: number): void;

  protected pixelsToTime(pixels: number) {
    if (!this.data) {
      return -1;
    }

    return (pixels * this.data.scale) / this.data.sample_rate;
  }

  protected timeToPixels(time: number) {
    if (!this.data) {
      return -1;
    }

    return Math.floor((time * this.data.sample_rate) / this.data.scale);
  }
}
